import { Avatar, Badge, Input, Typography } from "antd";
import { useTranslate, Trans } from "@obsbot/remo-i18n";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import { filterInputValue } from "@obsbot/remo-utils";
import { withMessage } from "../../HOC/withMessage";
import NoUserSVG from "./icons/no-user.webp";
import { CustomModal } from "../CustomModal";
import "./index.less";
import { ChannelInviteLinkCopySVG } from "../SvgIconCom";

export interface ChannelInviteProps {
    visible: boolean;
    sharelink: string;
    users: FriendUser[];
    isNoFriends?: boolean;
    onSearch: (searchContent: string) => void;
    onInvite: (user: FriendUser) => void;
    setVisible: (flag: boolean) => void;
    onClickAddUser: () => void;
    onInput?: (value: string) => void;
    onCopy?: () => Promise<void>;
}

export interface FriendUser {
    id: string;
    name: string;
    avatar: string;
    isOnline: boolean;
}

export const ChannelInviteModal = withMessage<ChannelInviteProps>(
    ({
        visible,
        users,
        sharelink,
        isNoFriends,
        message,
        onSearch,
        onInvite,
        onClickAddUser,
        setVisible,
        onInput,
        onCopy,
    }): ReactElement => {
        const t = useTranslate();
        const copyLinkRef = useRef<HTMLDivElement>(null);
        // 表单数据
        const [searchContent, setSearchContent] = useState("");
        // 重置
        useEffect(() => {
            setSearchContent("");
        }, [visible]);
        // 处理按下回车
        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
            if (e.key === "Enter") {
                handleOnSearch();
            }
        };
        // 处理onSearch
        const handleOnSearch = (): void => {
            onSearch && onSearch(searchContent);
        };
        // 渲染单个用户
        const FriendItem: React.FC<{ user: FriendUser }> = ({ user }): ReactElement => {
            return (
                <div className="friend-item">
                    <div className="friend-item-info">
                        <Badge
                            className="friend-online-dot"
                            color={user.isOnline ? "var(--dot-online)" : "var(--dot-offline)"}
                            dot={true}
                        >
                            <Avatar className="friend-avatar" src={user.avatar} />
                        </Badge>
                        <div className="friend-info">
                            <span className="friend-name">{user.name}</span>
                        </div>
                    </div>
                    <div className="invite-btn" onClick={() => onInvite && onInvite(user)}>
                        {t("invite")}
                    </div>
                </div>
            );
        };
        return (
            <CustomModal
                className="ChannelInviteModal"
                footer={null}
                height={414}
                open={visible}
                renderHeader={t("inviteFriends")}
                setVisible={setVisible}
                width={418}
                onClickClose={() => setSearchContent("")}
            >
                <div className="invite-modal-form-section">
                    {!isNoFriends && (
                        <Input
                            className="modal-form-input invite-modal-form-input"
                            placeholder={t("search")}
                            suffix={<SearchSVG />}
                            value={searchContent}
                            onChange={e => {
                                setSearchContent(filterInputValue(e.target.value));
                                onInput && onInput(e.target.value);
                            }}
                            onKeyDown={handleKeyDown}
                        />
                    )}
                </div>
                <div className="invite-modal-friends-list">
                    {isNoFriends ? (
                        <div className="invite-modal-friends-list-empty">
                            <div className="invite-modal-friends-list-empty-icon">
                                <img src={NoUserSVG} />
                            </div>
                            <div className="invite-modal-friends-list-empty-text">
                                <span>{t("noFriends")}</span>
                                <span>
                                    <Trans
                                        components={{
                                            friend: (
                                                <span
                                                    className="invite-modal-friends-to-add-user"
                                                    onClick={onClickAddUser}
                                                />
                                            ),
                                        }}
                                        i18nKey="addOne"
                                    />
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="invite-modal-friends-list-section">
                            {users.map((user: FriendUser) => (
                                <FriendItem key={user.id} user={user} />
                            ))}
                        </div>
                    )}
                </div>
                <div className="invite-modal-link-share">
                    <div className="invite-modal-title">{t("shareLink")}</div>
                    <div className="invite-modal-link-url">
                        {/* <Typography.Paragraph
                            ref={copyLinkRef}
                            copyable={{
                                icon: [<ChannelInviteLinkCopySVG />, <ChannelInviteLinkCopySVG />],
                                text: sharelink,
                                tooltips: false,
                                onCopy: () => {
                                    message.success(t("copySuccess"));
                                },
                            }}
                            onClick={() => {
                                (copyLinkRef.current?.children[0] as HTMLButtonElement)?.click();
                            }}
                        >
                            {t("copyLink")}
                        </Typography.Paragraph> */}
                        <Typography.Paragraph
                            ref={copyLinkRef}
                            onClick={async () => {
                                try {
                                    await onCopy?.();
                                    await navigator.clipboard.writeText(sharelink);
                                    message.success(t("copySuccess"));
                                } catch (err) {
                                    message.error(t("copyFailed"));
                                }
                            }}
                        >
                            <span>{t("copyLink")}</span>
                            <ChannelInviteLinkCopySVG />
                        </Typography.Paragraph>
                    </div>
                </div>
            </CustomModal>
        );
    },
);

const SearchSVG: React.FC = () => (
    <svg
        fill="none"
        height="20"
        style={{ cursor: "pointer" }}
        version="1.1"
        viewBox="0 0 20 20"
        width="20"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <defs>
            <clipPath id="master_svg0_341_8803">
                <rect height="20" rx="0" width="20" x="0" y="0" />
            </clipPath>
        </defs>
        <g clipPath="url(#master_svg0_341_8803)">
            <g>
                <path
                    d="M4.0433598375,4.0443364Q1.7490234975,6.338672900000001,1.7490234375,9.583333Q1.7490234375,12.827992,4.0433598375,15.12233Q6.3376972375000005,17.416666,9.5823564375,17.416666Q12.8270174375,17.416666,15.1213534375,15.12233Q17.4156894375,12.827991,17.4156894375,9.5833297Q17.4156894375,6.338672900000001,15.1213534375,4.0443364Q12.8270154375,1.75000006,9.5823531375,1.75Q6.337696837499999,1.75,4.0433598375,4.0443364ZM5.1040201375,14.061669Q3.2490234375,12.2066736,3.2490234375,9.583333Q3.2490235575,6.9599934,5.1040201375,5.1049967Q6.9590168375,3.25,9.5823531375,3.25Q12.2056961375,3.24999994,14.0606924375,5.1049967Q15.9156894375,6.9599924,15.9156894375,9.5833297Q15.9156894375,12.2066727,14.0606924375,14.061669Q12.2056980375,15.916666,9.5823564375,15.916666Q6.9590168375,15.916666,5.1040201375,14.061669Z"
                    fill="#BBBCC0"
                    fillOpacity="1"
                    fillRule="evenodd"
                />
            </g>
            <g>
                <path
                    d="M14.1464277525,15.20611131Q14.0409385525,15.10062212,13.9838482125,14.96279383Q13.9267578125,14.82496552,13.9267578125,14.67578125Q13.9267578125,14.601912707,13.9411689025,14.52946353Q13.9555799325,14.45701437,13.9838482125,14.38876873Q14.0121164925,14.32052308,14.0531556625,14.25910363Q14.0941948325,14.19768417,14.1464277525,14.14545119Q14.1986607325,14.09321827,14.2600801925,14.0521791Q14.3214996425,14.01113993,14.3897452925,13.98287165Q14.4579909325,13.95460337,14.5304400925,13.94019234Q14.6028892695,13.92578125,14.6767578125,13.92578125Q14.8259420825,13.92578125,14.9637703925,13.98287159Q15.1015986825,14.03996199,15.2070878725,14.14545119L18.7426720125,17.68103485Q18.8481607125,17.78652405,18.9052510125,17.92435215Q18.9623413125,18.06218055,18.9623413125,18.21136475Q18.9623413125,18.28523305,18.9479303125,18.35768225Q18.9335189125,18.43013115,18.9052505125,18.49837685Q18.8769817125,18.56662225,18.8359427125,18.62804175Q18.794903712500002,18.68946125,18.7426710125,18.74169445Q18.6904378125,18.793927150000002,18.6290183125,18.83496665Q18.5675988125,18.87600515,18.4993534125,18.90427395Q18.4311077125,18.93254235,18.3586585125,18.94695375Q18.2862096125,18.96136475,18.2123413125,18.96136475Q18.0631571125,18.96136475,17.9253290125,18.90427445Q17.7875008125,18.84718415,17.6820116125,18.74169495L14.1464276925,15.20611137L14.1464277525,15.20611131Z"
                    fill="#BBBCC0"
                    fillOpacity="1"
                    fillRule="evenodd"
                />
            </g>
        </g>
    </svg>
);
